import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute, AdminRoute, ReviewerRoute, HaleProtectedRoute, HaleStaffRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";

// Pages
import EnterpriseLanding from "./pages/EnterpriseLanding";
import BrandRoot from "./components/BrandRoot";
import LandingPage from "./pages/LandingPage";
import NotFoundPage from "./pages/NotFoundPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import UploadPage from "./pages/UploadPage";
import AssessmentPage from "./pages/AssessmentPage";
import LibraryPage from "./pages/LibraryPage";
import AssessorPage from "./pages/AssessorPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import TagcLanding from "./pages/TagcLanding";
import NoaelExtractionPage from "./pages/NoaelExtractionPage";
import ExposurePage from "./pages/ExposurePage";
import HazardIntelligencePage from "./pages/HazardIntelligencePage";
import PortfolioRequestPage from "./pages/PortfolioRequestPage";
import PortfolioTriagePage from "./pages/PortfolioTriagePage";
import HaleLandingPage from "./pages/HaleLandingPage";
import HaleDashboardPage from "./pages/HaleDashboardPage";
import HaleAdminHubPage from "./pages/admin/HaleAdminHubPage";
import HaleMarketingAssetsPage from "./pages/admin/HaleMarketingAssetsPage";
import HaleAuditSamplesPage from "./pages/admin/HaleAuditSamplesPage";
import ToxIntelSamplesPage from "./pages/admin/ToxIntelSamplesPage";
import HaleReviewerPage from "./pages/admin/HaleReviewerPage";
import HaleDecisionsPage from "./pages/admin/HaleDecisionsPage";
import HaleAdminPage from "./pages/HaleAdminPage";
import HaleVerifiedPage from "./pages/HaleVerifiedPage";
import SupplierSpecsPage from "./pages/SupplierSpecsPage";
import MarketplacePage from "./pages/MarketplacePage";
import MarketplaceMyLicensesPage from "./pages/MarketplaceMyLicensesPage";
import MarketplaceAdminPage from "./pages/MarketplaceAdminPage";
import HaleCommandPage from "./pages/HaleCommandPage";
import FormulaDbPage from "./pages/FormulaDbPage";
import HaleMarketplacePage from "./pages/HaleMarketplacePage";
import HaleApplyPage from "./pages/hale-marketplace/HaleApplyPage";
import HalePathwayPage from "./pages/hale-marketplace/HalePathwayPage";
import HaleMembershipPage from "./pages/hale-marketplace/HaleMembershipPage";
import HaleApplicationsAdminPage from "./pages/hale-marketplace/HaleApplicationsAdminPage";
import HaleInvoicesPage from "./pages/HaleInvoicesPage";
import CopilotPage from "./pages/CopilotPage";
import HaleLogin from "./pages/hale-console/HaleLogin";
import HaleRegister from "./pages/hale-console/HaleRegister";
import HaleApplicationsPage from "./pages/hale-console/HaleApplicationsPage";
import HaleReviewsPage from "./pages/hale-console/HaleReviewsPage";
import HaleDocumentsPage from "./pages/hale-console/HaleDocumentsPage";
import HaleProfilePage from "./pages/hale-console/HaleProfilePage";
import HaleProductsPage from "./pages/hale-console/HaleProductsPage";
import HaleReviewedQueue from "./pages/hale-admin/HaleReviewedQueue";
import HaleReviewedProduct from "./pages/hale-admin/HaleReviewedProduct";
import HaleConsolePage from "./pages/hale-console/HaleConsolePage";
import PassportPage from "./pages/PassportPage";
import CompliancePage from "./pages/admin/CompliancePage";
import AssetCenterPage from "./pages/admin/AssetCenterPage";
import InquiriesPage from "./pages/admin/InquiriesPage";
import OpsControlPage from "./pages/admin/OpsControlPage";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          {/* Root is build-time brand-aware (REACT_APP_BRAND). The deployment
              decides which brand it serves; no hostname inspection, no path
              forwarding, no URL-bar mutation. */}
          <Route path="/" element={<BrandRoot />} />
          <Route path="/about" element={<LandingPage />} />
          {/* Preview-only escape hatches — useful in dev/preview to inspect
              either brand without touching the build env. Production URL bars
              are not affected (each deployment's "/" already serves the right
              brand via BrandRoot). */}
          <Route path="/tagc" element={<TagcLanding />} />
          <Route path="/toxintel" element={<EnterpriseLanding />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/copilot" element={<ProtectedRoute><CopilotPage /></ProtectedRoute>} />
          <Route path="/passport" element={<ProtectedRoute><PassportPage /></ProtectedRoute>} />
          <Route path="/upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
          <Route path="/library" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />
          <Route path="/assessment/:id" element={<ProtectedRoute><AssessmentPage /></ProtectedRoute>} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
          <Route path="/admin/compliance" element={<AdminRoute><CompliancePage /></AdminRoute>} />
          <Route path="/admin/asset-center" element={<AdminRoute><AssetCenterPage /></AdminRoute>} />
          <Route path="/admin/inquiries" element={<AdminRoute><InquiriesPage /></AdminRoute>} />
          <Route path="/admin/ops" element={<AdminRoute><OpsControlPage /></AdminRoute>} />
          <Route path="/assessor" element={<AdminRoute><AssessorPage /></AdminRoute>} />
          <Route path="/assessor/noael-extraction" element={<AdminRoute><NoaelExtractionPage /></AdminRoute>} />
          <Route path="/assessor/exposure" element={<AdminRoute><ExposurePage /></AdminRoute>} />
          <Route path="/assessor/hazard-intelligence" element={<AdminRoute><HazardIntelligencePage /></AdminRoute>} />
          <Route path="/assessor/portfolio" element={<AdminRoute><PortfolioTriagePage /></AdminRoute>} />
          <Route path="/portfolio" element={<PortfolioRequestPage />} />
          <Route path="/hale" element={<HaleLandingPage />} />
          {/* Hale Brand Portal — white-labeled auth + console (reuses ToxIntel auth backend) */}
          <Route path="/hale/login" element={<HaleLogin />} />
          <Route path="/hale/register" element={<HaleRegister />} />
          <Route path="/hale/dashboard" element={<HaleProtectedRoute><HaleDashboardPage /></HaleProtectedRoute>} />
          <Route path="/hale/console" element={<HaleStaffRoute><HaleConsolePage /></HaleStaffRoute>} />
          <Route path="/hale/admin" element={<HaleStaffRoute><HaleConsolePage /></HaleStaffRoute>} />
          <Route path="/hale/applications" element={<HaleProtectedRoute><HaleApplicationsPage /></HaleProtectedRoute>} />
          <Route path="/hale/reviews" element={<HaleProtectedRoute><HaleReviewsPage /></HaleProtectedRoute>} />
          <Route path="/hale/reviewed" element={<HaleProtectedRoute><HaleReviewsPage /></HaleProtectedRoute>} />
          <Route path="/hale/documents" element={<HaleProtectedRoute><HaleDocumentsPage /></HaleProtectedRoute>} />
          <Route path="/hale/profile" element={<HaleProtectedRoute><HaleProfilePage /></HaleProtectedRoute>} />
          <Route path="/hale/products" element={<HaleProtectedRoute><HaleProductsPage /></HaleProtectedRoute>} />
          <Route path="/admin/hale-reviewed" element={<HaleStaffRoute><HaleReviewedQueue /></HaleStaffRoute>} />
          <Route path="/admin/hale-reviewed/:id" element={<HaleStaffRoute><HaleReviewedProduct /></HaleStaffRoute>} />
          <Route path="/hale/marketplace" element={<HaleMarketplacePage />} />
          <Route path="/hale/apply" element={<HaleApplyPage />} />
          <Route path="/hale/pathway/:id" element={<HalePathwayPage />} />
          <Route path="/hale/membership" element={<HaleMembershipPage />} />
          <Route path="/assessor/hale-applications" element={<AdminRoute><HaleApplicationsAdminPage /></AdminRoute>} />
          <Route path="/assessor/hale-invoices" element={<AdminRoute><HaleInvoicesPage /></AdminRoute>} />
          <Route path="/assessor/hale" element={<AdminRoute><HaleAdminPage /></AdminRoute>} />
          <Route path="/admin/hale" element={<AdminRoute><HaleAdminHubPage /></AdminRoute>} />
          <Route path="/toxintel/assessor/hale" element={<AdminRoute><HaleAdminHubPage /></AdminRoute>} />
          <Route path="/admin/hale/marketing" element={<AdminRoute><HaleMarketingAssetsPage /></AdminRoute>} />
          <Route path="/admin/hale/samples" element={<AdminRoute><HaleAuditSamplesPage /></AdminRoute>} />
          <Route path="/admin/toxintel/samples" element={<AdminRoute><ToxIntelSamplesPage /></AdminRoute>} />
          <Route path="/admin/hale/decisions" element={<AdminRoute><HaleDecisionsPage /></AdminRoute>} />
          <Route path="/assessor/hale-review" element={<ReviewerRoute><HaleReviewerPage /></ReviewerRoute>} />
          <Route path="/assessor/hale-verified" element={<AdminRoute><HaleVerifiedPage /></AdminRoute>} />
          <Route path="/assessor/supplier-specs" element={<AdminRoute><SupplierSpecsPage /></AdminRoute>} />
          <Route path="/assessor/hale-command" element={<AdminRoute><HaleCommandPage /></AdminRoute>} />
          <Route path="/assessor/formula-db" element={<AdminRoute><FormulaDbPage /></AdminRoute>} />

          {/* Marketplace */}
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/marketplace/my-licenses" element={<ProtectedRoute><MarketplaceMyLicensesPage /></ProtectedRoute>} />
          <Route path="/assessor/marketplace" element={<AdminRoute><MarketplaceAdminPage /></AdminRoute>} />
          
          {/* Fallback — 404 surface (also reused by AdminRoute for hide-on-anonymous) */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </AuthProvider>
  );
}

export default App;
